import {
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';

import {
    EzObject,
    EzBoolean,
    EzNumber,
    EzString,
    EzArray,
    EzFunction,
    EzUrl,
    EzJson,
    EzPromise
} from '/ezlibrary/helpers/EzHelpers.js';

import { EzClass } from '/ezlibrary/EzClass.js';

import {
    EzRegistrationState,
    EzGpsDataStatus
} from '/ezlibrary/enums/EzEnumerations.js';

import { EzDateTime } from '/public/javascript/common/ez-date-time.js';
import { EzHttpHelper } from '/public/javascript/common/ezclocker-http-helper.js';
import { EzNavigation } from '/public/javascript/common/ezclocker-navigation-helper.js';

/**
 * @class
 * @description
 * Time entry payload entity
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *  import { EzTimeEntryPayload } from '/secure/javascript/services/ezclocker-time-entry-service.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzTimeEntryPayload {
    /**
     * Constructor
     * @param {null|number} employerId
     * @param {null|number} employeeId
     * @param {null|string} clockInIso
     * @param {null|string} clockOutIso
     * @param {null|string} notes
     */
    constructor(employerId, employeeId, clockInIso, clockOutIso, notes) {
        this.employerId = EzNumber.numberOrNull(employerId);

        this.employeeId = EzNumber.numberOrNull(employeeId);

        this.clockInIso = EzString.stringOrNull(clockInIso);

        this.clockInTimeZoneId = EzString.stringOrNull(clockInIso);

        this.clockOutIso = EzString.stringOrNull(clockOutIso);

        this.clockOutTimeZoneId = EzString.stringOrNull(clockOutIso);

        this.notes = EzString.stringOrEmpty(notes);

        this.targetDateTimeZone = globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone;

        this.locTime = globalThis.ezApi.ezclocker.ezDateTime.ezToIso(
            globalThis.ezApi.ezclocker.ezDateTime.ezNow());
    }

    /**
     * @private @field
     * Stores the employerId
     * @type {number}
     */
    #employerId = null;
    /**
     * @public @property @getter
     * Gets
     * @returns {number}
     */
    get employerId() {
        return this.#employerId;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {number} employerId;
     */
    set employerId(employerId) {
        this.#employerId = employerId;
    }

    /**
     * @private @field
     * Stores
     * @type {number}
     */
    #employeeId = null;
    /**
     * @public @property @getter
     * Gets
     * @returns {number}
     */
    get employeeId() {
        return this.#employeeId;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {number} employeeId;
     */
    set employeeId(employeeId) {
        this.#employeeId = employeeId;
    }

    /**
     * @private @field
     * Stores
     * @type {number}
     */
    #scheduleId = null;
    /**
     * @public @property @getter
     * Gets
     * @returns {number}
     */
    get scheduleId() {
        return this.#scheduleId;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {number} scheduleId;
     */
    set scheduleId(scheduleId) {
        this.#scheduleId = scheduleId;
    }

    /**
     * @private @field
     * Stores
     * @type {number}
     */
    #associatedScheduleId = null;
    /**
     * @public @property @getter
     * Gets
     * @returns {number}
     */
    get associatedScheduleId() {
        return this.#associatedScheduleId;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {number} employerId;
     */
    set associatedScheduleId(associatedScheduleId) {
        this.#associatedScheduleId = associatedScheduleId;
    }

    /**
     * @private @field
     * Stores
     * @type {number}
     */
    #customerId = null;
    /**
     * @public @property @getter
     * Gets
     * @returns {number}
     */
    get customerId() {
        return this.#customerId;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {number} customerId;
     */
    set customerId(customerId) {
        this.#customerId = customerId;
    }

    /**
     * @private @field
     * Stores
     * @type {number}
     */
    #clockInIso = null;
    /**
     * @public @property @getter
     * Gets
     * @returns {number}
     */
    get clockInIso() {
        return this.#clockInIso;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {number} clockInIso;
     */
    set clockInIso(scheduleId) {
        this.#clockInIso = scheduleId;
    }

    /**
     * @private @field
     * Stores
     * @type {number}
     */
    #serverClockInIso = null;
    /**
     * @public @property @getter
     * Gets
     * @returns {number}
     */
    get serverClockInIso() {
        return this.#serverClockInIso;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {number} serverClockInIso;
     */
    set serverClockInIso(serverClockInIso) {
        this.#serverClockInIso = serverClockInIso;
    }

    /**
     * @private @field
     * Stores
     * @type {string}
     */
    #clockInTimeZoneId = 'UTC';
    /**
     * @public @property @getter
     * Gets
     * @returns {string}
     */
    get clockInTimeZoneId() {
        return this.#clockInTimeZoneId;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {string} clockInTimeZoneId;
     */
    set clockInTimeZoneId(clockInTimeZoneId) {
        this.#clockInTimeZoneId = EzString.stringWithLengthOrDefault(
            clockInTimeZoneId,
            'UTC');
    }

    /**
     * @private @field
     * Stores
     * @type {string}
     * A valid enum property value from EzGpsDataStatus
     */
    #clockInGpsStatus = EzGpsDataStatus.UNKNOWN;
    /**
     * @public @property @getter
     * Gets
     * @returns {string}
     * A valid enum property value from EzGpsDataStatus
     */
    get clockInGpsStatus() {
        return this.#clockInGpsStatus;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {string} clockInGpsStatus;
     * A valid enum property value from EzGpsDataStatus
     */
    set clockInGpsStatus(clockInGpsStatus) {
        this.#clockInGpsStatus = EzGpsDataStatus.ezAsEnum(clockInGpsStatus);
    }

    /**
     * @private @field
     * Stores
     * @type {number}
     */
    #clockOutIso = null;
    /**
     * @public @property @getter
     * Gets
     * @returns {number}
     */
    get clockOutIso() {
        return this.#clockOutIso;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {number} clockOutIso;
     */
    set clockOutIso(clockOutIso) {
        this.#clockOutIso = clockOutIso;
    }

    /**
     * @private @field
     * Stores
     * @type {number}
     */
    #serverClockOutIso = null;
    /**
     * @public @property @getter
     * Gets
     * @returns {number}
     */
    get serverClockOutIso() {
        return this.#serverClockOutIso;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {number} serverClockOutIso;
     */
    set serverClockOutIso(serverClockOutIso) {
        this.#serverClockOutIso = serverClockOutIso;
    }

    /**
     * @private @field
     * Stores
     * @type {string}
     */
    #clockOutTimeZoneId = 'UTC';
    /**
     * @public @property @getter
     * Gets
     * @returns {string}
     */
    get clockOutTimeZoneId() {
        return this.#clockOutTimeZoneId;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {string} clockOutTimeZoneId;
     */
    set clockOutTimeZoneId(clockOutTimeZoneId) {
        this.#clockOutTimeZoneId = EzString.stringWithLengthOrDefault(
            clockOutTimeZoneId,
            'UTC');
    }

    /**
     * @private @field
     * Stores
     * @type {string}
     * A valid enum property value from EzGpsDataStatus
     */
    #clockOutGpsStatus = EzGpsDataStatus.UNKNOWN;
    /**
     * @public @property @getter
     * Gets
     * @returns {string}
     * A valid enum property value from EzGpsDataStatus
     */
    get clockOutGpsStatus() {
        return this.#clockOutGpsStatus;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {string} clockOutGpsStatus;
     * A valid enum property value from EzGpsDataStatus
     */
    set clockOutGpsStatus(clockOutGpsStatus) {
        this.#clockOutGpsStatus = EzGpsDataStatus.ezAsEnum(clockOutGpsStatus);
    }

    /**
     * @private @field
     * Stores
     * @type {boolean}
     */
    #isActiveClockIn = false;
    /**
     * @public @property @getter
     * Gets
     * @returns {boolean}
     */
    get isActiveClockIn() {
        return this.#isActiveClockIn;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {boolean} isActiveClockIn;
     */
    set isActiveClockIn(isActiveClockIn) {
        this.#isActiveClockIn = EzBoolean.booleanOrFalse(isActiveClockIn);
    }

    /**
     * @private @field
     * Stores
     * @type {boolean}
     */
    #offLineSync = false;
    /**
     * @public @property @getter
     * Gets
     * @returns {boolean}
     */
    get offLineSync() {
        return this.#offLineSync;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {boolean} offLineSync;
     */
    set offLineSync(offLineSync) {
        this.#offLineSync = EzBoolean.booleanOrFalse(offLineSync);
    }

    /**
     * @private @field
     * Stores
     * @type {boolean}
     */
    #earlyClockInOverride = false;
    /**
     * @public @property @getter
     * Gets
     * @returns {boolean}
     */
    get earlyClockInOverride() {
        return this.#earlyClockInOverride;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {boolean} earlyClockInOverride;
     */
    set earlyClockInOverride(earlyClockInOverride) {
        this.#earlyClockInOverride = EzBoolean.booleanOrFalse(earlyClockInOverride);
    }

    /**
     * @private @field
     * Stores
     * @type {boolean}
     */
    #paid = false;
    /**
     * @public @property @getter
     * Gets
     * @returns {boolean}
     */
    get paid() {
        return this.#paid;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {boolean} paid;
     */
    set paid(paid) {
        this.#paid = EzBoolean.booleanOrFalse(paid);
    }

    /**
     * @private @field
     * Stores
     * @type {string}
     */
    #notes = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets
     * @returns {string}
     */
    get notes() {
        return this.#notes;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {string} notes;
     */
    set notes(notes) {
        this.#notes = EzString.stringOrEmpty(notes);
    }

    /**
     * @private @field
     * Stores
     * @type {string}
     */
    #description = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets
     * @returns {string}
     */
    get description() {
        return this.#description;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {string} description;
     */
    set description(description) {
        this.#description = EzString.stringOrEmpty(description);
    }

    /**
     * @private @field
     * Stores
     * @type {number|string}
     */
    #modifiedBy = null;
    /**
     * @public @property @getter
     * Gets
     * @returns {number|string}
     */
    get modifiedBy() {
        return this.#modifiedBy;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {number|string} modifiedBy;
     */
    set modifiedBy(modifiedBy) {
        this.#modifiedBy = modifiedBy;
    }

    /**
     * @private @field
     * Stores
     * @type {array}
     */
    #assignedJobs = EzArray.EMPTY;
    /**
     * @public @property @getter
     * Gets
     * @returns {array}
     */
    get assignedJobs() {
        return this.#assignedJobs;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {array} assignedJobs;
     */
    set assignedJobs(assignedJobs) {
        this.#assignedJobs = EzArray.arrayOrEmpty(assignedJobs);
    }

    /**
     * @private @field
     * Stores
     * @type {array}
     */
    #jobMappings = null;
    /**
     * @public @property @getter
     * Gets
     * @returns {array}
     */
    get jobMappings() {
        return this.#jobMappings;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {array} jobMappings;
     */
    set jobMappings(jobMappings) {
        this.#jobMappings = EzArray.arrayOrEmpty(jobMappings);
    }

    /**
     * @private @field
     * Stores
     * @type {string}
     */
    #targetDateTimeZone = 'UTC';
    /**
     * @public @property @getter
     * Gets
     * @returns {string}
     */
    get targetDateTimeZone() {
        return this.#targetDateTimeZone;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {string} targetDateTimeZone;
     */
    set targetDateTimeZone(targetDateTimeZone) {
        this.#targetDateTimeZone = targetDateTimeZone;
    }

    /**
     * @private @field
     * Stores
     * @type {number}
     */
    #doNotAudit = false;
    /**
     * @public @property @getter
     * Gets
     * @returns {number}
     */
    get doNotAudit() {
        return this.#doNotAudit;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {number} doNotAudit;
     */
    set doNotAudit(doNotAudit) {
        this.#doNotAudit = doNotAudit;
    }

    /**
     * @private @field
     * Stores
     * @type {number}
     */
    #overrideLocationCheck = null;
    /**
     * @public @property @getter
     * Gets
     * @returns {number}
     */
    get overrideLocationCheck() {
        return this.#overrideLocationCheck;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {number} employerId;
     */
    set overrideLocationCheck(overrideLocationCheck) {
        this.#overrideLocationCheck = overrideLocationCheck;
    }

    /**
     * @private @field
     * Stores
     * @type {array}
     */
    #dataTagMaps = EzArray.EMPTY;
    /**
     * @public @property @getter
     * Gets
     * @returns {array}
     */
    get dataTagMaps() {
        return this.#dataTagMaps;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {array} dataTagMaps;
     */
    set dataTagMaps(dataTagMaps) {
        this.#dataTagMaps = EzArray.arrayOrEmpty(dataTagMaps);
    }

    /**
     * @private @field
     * Stores
     * @type {string}
     */
    #locTime = null;
    /**
     * @public @property @getter
     * Gets
     * @returns {string}
     */
    get locTime() {
        return this.#locTime;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {string} locTime;
     */
    set locTime(locTime) {
        this.#locTime = locTime;
    }

    /**
     * @private @field
     * Stores
     * @type {string|number}
     */
    #accuracy = '0';
    /**
     * @public @property @getter
     * Gets
     * @returns {string|number}
     */
    get accuracy() {
        return this.#accuracy;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {string|number} accuracy;
     */
    set accuracy(accuracy) {
        this.#accuracy = accuracy;
    }

    /**
     * @private @field
     * Stores
     * @type {string|number}
     */
    #latitude = '0';
    /**
     * @public @property @getter
     * Gets
     * @returns {string|number}
     */
    get latitude() {
        return this.#latitude;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {string|number} latitude;
     */
    set latitude(latitude) {
        this.#latitude = latitude;
    }

    /**
     * @private @field
     * Stores
     * @type {string|number}
     */
    #longitude = '0';
    /**
     * @public @property @getter
     * Gets
     * @returns {string|number}
     */
    get longitude() {
        return this.#longitude;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {string|number} longitude;
     */
    set longitude(longitude) {
        this.#longitude = longitude;
    }

    /**
     * @private @field
     * Stores
     * @type {string|number}
     */
    #radius = null;
    /**
     * @public @property @getter
     * Gets
     * @returns {number}
     */
    get radius() {
        return this.#radius;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {number} radius;
     */
    set radius(radius) {
        this.#radius = radius;
    }

    /**
     * @private @field
     * Stores
     * @type {string|number}
     */
    #altitude = '0';
    /**
     * @public @property @getter
     * Gets
     * @returns {string|number}
     */
    get altitude() {
        return this.#altitude;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {string|number} employerId;
     */
    set altitude(altitude) {
        this.#altitude = altitude;
    }

    /**
     * @private @field
     * Stores
     * @type {string|number}
     */
    #speed = '0';
    /**
     * @public @property @getter
     * Gets
     * @returns {string|number}
     */
    get speed() {
        return this.#speed;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {string|number} employerId;
     */
    set speed(speed) {
        this.#speed = speed;
    }

    /**
     * @private @field
     * Stores
     * @type {string}
     */
    #gpsSource = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets
     * @returns {string}
     */
    get gpsSource() {
        return this.#gpsSource;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {string} employerId;
     */
    set gpsSource(gpsSource) {
        this.#gpsSource = gpsSource;
    }

    /**
     * @private @field
     * Stores
     * @type {string}
     * A valid enum property value from EzGpsDataStatus
     */
    #gpsDataStatus = EzGpsDataStatus.UNKNOWN;
    /**
     * @public @property @getter
     * Gets
     * @returns {string}
     * A valid enum property value from EzGpsDataStatus
     */
    get gpsDataStatus() {
        return this.#gpsDataStatus;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {string} gpsDataStatus;
     * A valid enum property value from EzGpsDataStatus
     */
    set gpsDataStatus(gpsDataStatus) {
        this.#gpsDataStatus = EzGpsDataStatus.ezAsEnum(gpsDataStatus);
    }

    /**
     * @private @field
     * Stores
     * @type {number}
     */
    #source = 'EZCLOCKER-WEBSITE';
    /**
     * @public @property @getter
     * Gets
     * @returns {string}
     */
    get source() {
        return this.#source;
    }
    /**
     * @public @property @setter
     * Sets
     * @param {string} employerId;
     */
    set source(source) {
        this.#source = EzString.stringWithLengthOrDefault(
            source,
            'EZCLOCKER-WEBSITE');
    }

    employerId = null;

    employeeId = null;

    scheduleId = null;

    associatedScheduleId = null;

    customerId = null;

    clockInIso = null;

    serverClockInIso = null;

    clockInTimeZoneId = null;

    clockInGpsStatus = 'UNKNOWN';

    clockInGpsDataStatus = 'UNKNOWN';

    clockOutIso = null;

    serverClockOutIso = null;

    clockOutTimeZoneId = null;

    clockOutGpsStatus = 'UNKNOWN';

    clockOutGpsDataStatus = 'UNKNOWN';

    isActiveClockIn = -false;

    offLineSync = false;

    earlyClockInOverride = false;

    paid = false;

    notes = EzString.EMPTY;

    description = EzString.EMPTY;

    modifiedBy = null;

    assignedJobs = null;

    jobMappings = null;

    targetDateTimeZone = null;

    doNotAudit = false;

    overrideLocationCheck = null;

    dataTagMaps = null;

    locTime = null;

    accuracy = '0';

    latitude = '0';

    longitude = '0';

    radius = null;

    altitude = '0';

    speed = '0';

    gpsSource = EzString.EMPTY;

    gpsDataStatus = 'UNKNOWN';

    source = 'website';
}

/**
 * @class
 * @extends {EzClass}
 * @description
 * Provides operations on time entries
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzTimeEntryService } from '/secure/javascript/services/ezclocker-time-entry-service.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *     globalThis.ezApi.ezclocker?.[EzTimeEntryService.ezApiName]?.ready
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *     document.addEventListener(
 *         EzTimeEntryService.ezEventNames.onReady,
 *         EzClockerContext.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzTimeEntryService extends EzClass {
    /**
     * @public @readonly @property
     * Gets the _api/v1/timeentry base url constant string
     * @returns {string}
     */
    get INTERNAL_TIMEENTRIES_V1_REST_SERVICE_URL_BASE() {
        return globalThis.ezApi.ezclocker.ezNavigation.ezGetInternalApiUrl('timeentry', 'v1');
    }

    /**
     * @public @readonly @property
     * Gets the constant string `${this.INTERNAL_TIMEENTRIES_V1_REST_SERVICE_URL_BASE}/active`
     * @returns {string}
     */
    get INTERNAL_TIMEENTRIES_V1_GET_ACTIVE_TIMEENTRY_URL() {
        return `${this.INTERNAL_TIMEENTRIES_V1_REST_SERVICE_URL_BASE}/active`;
    }

    /**
     * @public @readonly @property
     * Gets the constant string `${this.INTERNAL_TIMEENTRIES_V1_REST_SERVICE_URL_BASE}/active-break`
     * @returns {string}
     */
    get INTERNAL_TIMEENTRIES_V1_GET_ACTIVE_BREAKIN_TIMEENTRY_URL() {
        return `${this.INTERNAL_TIMEENTRIES_V1_REST_SERVICE_URL_BASE}/active-break`;
    }

    /**
     * @public @readonly @property
     * Gets the constant string `${this.INTERNAL_TIMEENTRIES_V1_REST_SERVICE_URL_BASE}/clockin`
     * @returns {string}
     */
    get INTERNAL_TIMEENTRIES_V1_CLOCKIN_URL() {
        return `${this.INTERNAL_TIMEENTRIES_V1_REST_SERVICE_URL_BASE}/clockin`;
    }

    /**
     * @public @readonly @property
     * Gets the constant string `${this.INTERNAL_TIMEENTRIES_V1_REST_SERVICE_URL_BASE}/clockout`
     * @returns {string}
     */
    get INTERNAL_TIMEENTRIES_V1_CLOCKOUT_URL() {
        return `${this.INTERNAL_TIMEENTRIES_V1_REST_SERVICE_URL_BASE}/clockout`;
    }

    /**
     * @public @readonly @property
     * Gets the constant string globalThis.ezApi.ezclocker.ezNavigation.ezGetInternalClassicApiUrl('timeEntry')
     * @returns {string}
     */
    get INTERNAL_TIMEENTRY_SERVICE_URL_BASE() {
        return globalThis.ezApi.ezclocker.ezNavigation.ezGetInternalClassicApiUrl('timeEntry');
    }

    /**
     * @public @readonly @property
     * Gets the constant string `${this.INTERNAL_TIMEENTRY_SERVICE_URL_BASE}/isClockedIn`
     * @returns {string}
     */
    get INTERNAL_TIMEENTRY_ISCLOCKEDIN_URL() {
        return `${this.INTERNAL_TIMEENTRY_SERVICE_URL_BASE}/isClockedIn`;
    }

    /**
     * @public @readonly @property
     * Gets the constant string `${this.INTERNAL_TIMEENTRY_SERVICE_URL_BASE}/clockIn`
     * @returns {string}
     */
    get INTERNAL_TIMEENTRY_CLOCKIN_URL() {
        return `${this.INTERNAL_TIMEENTRY_SERVICE_URL_BASE}/clockIn`;
    }

    /**
     * @public @readonly @property
     * Gets the constant string `${this.INTERNAL_TIMEENTRY_SERVICE_URL_BASE}/clockOut`
     * @returns {string}
     */
    get INTERNAL_TIMEENTRY_CLOCKOUT_URL() {
        return `${this.INTERNAL_TIMEENTRY_SERVICE_URL_BASE}/clockOut`;
    }

    /**
     * @public @method
     * Initializes EzTimeEntryService
     * @returns {EzTimeEntryService}
     */
    ezInit() {
        return EzTimeEntryService.ezInstance;
    }

    /**
     * @public @method
     * Obtains the time entries for an employee based on the period information provided.
     *
     * @param {number} employeeId
     * @param {string} startDateIso
     * @param {string} endDateIso
     * @returns {Promise}
     */
    ezQueryEmployeeTimeEntriesForPeriod(employeeId, startDateIso, endDateIso) {
        if (!EzNumber.isNumber(employeeId)) {
            throw new EzBadParamException(
                'employeeId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezQueryEmployeeTimeEntriesForPeriod);
        }
        if (!EzString.hasLength(startDateIso)) {
            throw new EzBadParamException(
                'startDateIso',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezQueryEmployeeTimeEntriesForPeriod);
        }
        if (!EzString.hasLength(endDateIso)) {
            throw new EzBadParamException(
                'endDateIso',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezQueryEmployeeTimeEntriesForPeriod);
        }

        return globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
            globalThis.ezApi.ezclocker.ezNavigation.ezGetInternalApiUrl(
                EzUrl.build`
                    timeentry/employees/${employeeId}
                     ?start-date-iso=${startDateIso}
                     &end-date-iso=${endDateIso}
                     &time-zone-id=${globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone}`,
                'v2'))
            .then(
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject);
    }

    /**
     * @public @method
     * Gets the time entries for the provided employee during the provided period
     * @param {number} employerId
     * @param {number} employeeId
     * @param {moment} periodStartMoment
     * @param {moment} periodEndMoment
     *
     * @returns {Promise}
     */
    ezGetEmployeeTimeEntriesForPeriod(employerId, employeeId, periodStartMoment, periodEndMoment) {
        if (!EzNumber.isNumber(employeeId)) {
            throw new EzBadParamException(
                'employeeId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezGetEmployeeTimeEntriesForPeriod);
        }
        if (!EzNumber.isNumber(employeeId)) {
            throw new EzBadParamException(
                'employeeId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezGetEmployeeTimeEntriesForPeriod);
        }
        if (!EzObject.isValid(periodStartMoment)) {
            throw new EzBadParamException(
                'periodStartMoment',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezGetEmployeeTimeEntriesForPeriod);
        }
        if (!EzObject.isValid(periodEndMoment)) {
            throw new EzBadParamException(
                'periodStartMoment',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezGetEmployeeTimeEntriesForPeriod);
        }

        return globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
            globalThis.ezApi.ezclocker.ezNavigation.getInternalClassicApiUrl(
                EzUrl.build`
                    timeEntry/getPeriod/${employerId}/${employeeId}
                    ?startDateIso8601=${periodStartMoment.toISOString()}
                    &endDateIso8601=${periodEndMoment.toISOString()}
                    &timeZoneId=${globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone}`));
    }

    /**
     * @public @method
     * Quries time entries for the provided employerId and employeeId for the provided periodStartMoment and periodEndMoment
     * date range.
     *
     * @param {number} employerId
     * @param {number} employeeId
     * @param {moment} periodStartMoment
     * @param {moment} periodEndMoment
     *
     * @returns {Promise}
     */
    ezQuery(employerId, employeeId, periodStartMoment, periodEndMoment) {
        if (!EzNumber.isNumber(employerId)) {
            throw new EzBadParamException(
                'employerId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezQuery);
        }
        if (!EzNumber.isNumber(employeeId)) {
            throw new EzBadParamException(
                'employeeId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezQuery);
        }
        if (!EzObject.isValid(periodStartMoment)) {
            throw new EzBadParamException(
                'periodStartMoment',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezQuery);
        }
        if (!EzObject.isValid(periodEndMoment)) {
            throw new EzBadParamException(
                'periodEndMoment',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezQuery);
        }

        return globalThis.ezApi.ezclocker.ezHttpHelper.ezPost(
            globalThis.ezApi.ezclocker.ezNavigation.ezGetInternalApiUrl(`timeentry/period/${employeeId}`, 'v2'),
            EzJson.toJson({
                customerId: null,
                periodStartDateIso: globalThis.ezApi.ezclocker.ezDateTime.ezToIso(
                    globalThis.ezApi.ezclocker.ezDateTime.ezSetMomentStartOfDay(periodStartMoment)),
                periodEndDateIso: globalThis.ezApi.ezclocker.ezDateTime.ezToIso(
                    globalThis.ezApi.ezclocker.ezDateTime.ezSetMomentEndOfDay(periodEndMoment)),
                targetTimeZoneId: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
            }))
            .then(
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject);
    }

    /**
     * @public @method
     * Obtains the employee's active clock in (if any)
     *
     * @param {number} employerId
     * @param {number} employeeId
     * @param {string} timezone
     * @param {undefined|null|function} success
     * @param {undefined|null|function} failure
     *
     * @returns {Promise}
     */
    ezGetActiveClockIn(employeeId) {
        if (!EzNumber.isNumber(employeeId)) {
            throw new EzBadParamException(
                'employeeId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezGetActiveClockIn);
        }

        return globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
            globalThis.ezApi.ezclocker.ezNavigation.ezGetInternalApiUrl(
                `timeentry/active/${employeeId}?time-zone-id=${globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone}`,
                'v2'))
            .then(
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject);
    }

    /**
     * @public @method
     * Obtains the employee's active clock in (if any)
     *
     * @param {Number} employerId
     * @param {Number} employeeId
     * @param {String} timezone
     * @param {Function|null} success
     * @param {Function|null} failure
     *
     * @returns {Promise}
     */
    ezGetActiveBreakIn(employeeId) {
        if (!EzNumber.isNumber(employeeId)) {
            throw new EzBadParamException(
                'employeeId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezGetActiveBreakIn);
        }

        return globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
            EzUrl.build`
                ${EzTimeEntryService.ezInstance.INTERNAL_TIMEENTRIES_V1_GET_ACTIVE_BREAKIN_TIMEENTRY_URL}
                /${employeeId}
                ?target-time-zone-id=${globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone}`)
            .then(
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject);
    }

    /**
     * @public @method
     * Obtains the employee's active clock in (if any)
     *
     * @param {Number} employerId
     * @param {Number} employeeId
     * @param {String} timezone
     * @param {undefined|null|function} success
     * Default: EzFunction.dummyFunction
     * @param {undefined|null|function} failure
     * Default: EzFunction.dummyFunction
     *
     * @returns {Promise}
     *
     * @deprecated
     * Migrate to globalThis.ezApi.ezclocker.ezTimeEntryService.ezGetActiveClockIn(...)
     */
    getActiveClockIn(employeeId, success = EzFunction.dummyFunction, failure = EzFunction.dummyFunction) {
        if (!EzNumber.isNumber(employeeId)) {
            throw new EzBadParamException(
                'employeeId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezClockIn);
        }

        EzTimeEntryService.ezInstance.ezGetActiveClockIn(employeeId)
            .then(
                (response, jqXHR) => EzFunction.callback(success, response, jqXHR),
                (eResponse, jqXHR) => EzFunction.callback(failure, eResponse, jqXHR));
    }

    /**
     * @public @method
     *
     * @param {number} employerId
     * @param {number} employeeId
     * @param {undefined|null|function} success
     * Default: EzFunction.dummyFunction
     * @param {undefined|null|function} failure
     * Default: EzFunction.dummyFunction
     *
     * @returns {Promise}
     */
    ezClockIn(employerId, employeeId, success = EzFunction.dummyFunction, failure = EzFunction.dummyFunction) {
        if (!EzNumber.isNumber(employerId)) {
            throw new EzBadParamException(
                'employerId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezClockIn);
        }
        if (!EzNumber.isNumber(employeeId)) {
            throw new EzBadParamException(
                'employeeId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezClockIn);
        }

        const payload = new EzTimeEntryPayload(employerId, employeeId);

        payload.clockInIso = globalThis.ezApi.ezclocker.ezDateTime.ezNow().toISOString();

        payload.clockOutIso = payload.clockInIso;

        payload.paid = false;

        payload.notes = EzString.EMPTY;

        payload.description = EzString.EMPTY;

        payload.overrideLocationCheck = true;

        payload.targetDateTimeZone = globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone;

        payload.source = 'EZCLOCKER-WEBSITE';

        return EzPromise.promise(
            (resolve, reject) => globalThis.ezApi.ezclocker.ezHttpHelper.ezPost(
                globalThis.ezApi.ezclocker.ezNavigation.getInternalApiServiceUrl(
                    'timeentry/clock-in',
                    'v2'),
                EzJson.toJson(payload))
                .then(
                    (response, jqXHR) => {
                        EzFunction.callback(success, response, jqXHR);

                        return resolve(response, jqXHR);
                    },
                    (eResponse, jqXHR) => {
                        EzFunction.callback(failure, eResponse, jqXHR);

                        reject(eResponse, jqXHR);
                    }));
    }

    /**
     * @public @method
     *
     * @param {number} employerId
     * @param {number} employeeId
     * @param {string} clockInDateTimeIso
     * @param {undefined|null|function} success
     * @param {undefined|null|function} failure
     * Default: EzFunction.dummyFunction
     * @param {undefined|null|string} source
     * Default: 'EZCLOCKER-WEBSITE'
     *
     * @returns {Promise}
     */
    ezClockOut(employerId, employeeId, clockInDateTimeIso, success = EzFunction.dummyFunction, failure = EzFunction.dummyFunction,
        source = 'EZCLOCKER-WEBSITE') {
        if (!EzNumber.isNumber(employerId)) {
            throw new EzBadParamException(
                'employerId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezClockIn);
        }
        if (!EzNumber.isNumber(employeeId)) {
            throw new EzBadParamException(
                'employeeId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezClockIn);
        }
        if (!EzString.hasLength(clockInDateTimeIso)) {
            throw new EzBadParamException(
                'clockInDateTimeIso',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezClockIn);
        }

        source = EzString.stringWithLengthOrDefault(
            source,
            'EZCLOCKER-WEBSITE');

        const payload = new EzTimeEntryPayload(employerId, employeeId);

        payload.clockInIso = clockInDateTimeIso;

        payload.clockInGpsStatus = 'NOT_SUPPORTED';

        payload.clockOutIso = globalThis.ezApi.ezclocker.ezDateTime.ezNow().toISOString();

        payload.clockOutGpsStatus = 'NOT_SUPPORTED';

        payload.gpsDataStatus = 'NOT_SUPPORTED';

        payload.paid = false;

        payload.notes = EzString.EMPTY;

        payload.description = EzString.EMPTY;

        payload.overrideLocationCheck = true;

        return EzPromise.promise(
            (resolve, reject) => globalThis.ezApi.ezclocker.ezHttpHelper.ezPost(
                globalThis.ezApi.ezclocker.ezNavigation.getInternalApiServiceUrl(
                    EzUrl.build`
                        timeentry/clockout/${employerId}/${employeeId}
                        ?timezone=${globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone}
                        &source=${source}`,
                    'v1'),
                EzJson.toJson(payload))
                .then(
                    (response, jqXHR) => {
                        EzFunction.callback(success, response, jqXHR);

                        return resolve(response, jqXHR);
                    },
                    (eResponse, jqXHR) => {
                        EzFunction.callback(failure, eResponse, jqXHR);

                        return reject(eResponse, jqXHR);
                    }));
    }

    /**
     * @public @method
     * Updates the provided timeEntry entity
     *
     * @param {Object} timeEntry
     * @param {undefined|null|boolean} overrideAuditRecording
     * Default: false
     *
     * @returns {Promise}
    */
    ezUpdateTimeEntry(timeEntry, overrideAuditRecording = false) {
        if (!EzObject.isValid(timeEntry) || !EzNumber.isNumber(timeEntry.id)) {
            throw new EzBadParamException(
                'timeEntry',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.ezUpdateTimeEntry);
        }

        timeEntry.doNotAudit = EzBoolean.booleanOrFalse(overrideAuditRecording);

        return globalThis.ezApi.ezclocker.ezHttpHelper.ezPut(
            globalThis.ezApi.ezclocker.ezNavigation.getInternalApiServiceUrl(`timeentry/${timeEntry.id}`, 'v2'),
            EzJson.toJson(timeEntry))
            .then(
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject);
    }

    /**
     * @public @method
     *
     * @param {number} employerId
     * @param {number} employeeId
     * @param {object} position
     * @param {boolean} overrideLocationCheck
     * Defualt: false
     * @param {function|null} success
     * Defualt: EzFunction.dummyFunction
     * @param {function|null} failure
     * Defualt: EzFunction.dummyFunction
     * @param {string|null} source
     * Default: 'EZCLOCKER-WEBSITE'
     *
     * @returns {Promise}
     */
    clockInGeoNonRest(employerId, employeeId, position, overrideLocationCheck = false, success = EzFunction.dummyFunction, failure = EzFunction.dummyFunction,
        source = 'EZCLOCKER-WEBSITE') {
        if (!EzNumber.isNumber(employerId)) {
            throw new EzBadParamException(
                'employerId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.clockOutGeoNonRest);
        }
        if (!EzNumber.isNumber(employeeId)) {
            throw new EzBadParamException(
                'employeeId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.clockOutGeoNonRest);
        }
        if (!EzObject.isValid(position)) {
            throw new EzBadParamException(
                'position',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.clockOutGeoNonRest);
        }

        source = EzString.stringWithLengthOrDefault(
            source,
            'EZCLOCKER-WEBSITE');

        return EzTimeEntryService.ezInstance.ezPostAndHandleResolveRejectCallback(
            EzUrl.build`
                ${EzTimeEntryService.ezInstance.INTERNAL_TIMEENTRY_CLOCKIN_URL}/${employerId}/${employeeId}
                ?clockInISO8601Utc=${globalThis.ezApi.ezclocker.ezDateTime.ezNow().toISOString()}
                &timeZoneId=${globalThis.ezApi.ezEncode(globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone)}
                &longitude=${position.coords.longitude}
                &latitude=${position.coords.latitude}
                &accuracy=${position.coords.Accuracy}
                &overrideLocationCheck=${overrideLocationCheck}
                &source=${source}`,
            EzString.EMPTY,
            success,
            failure);
    }

    /**
     * @public @method
     *
     * @param {number} employerId
     * @param {number} employeeId
     * @param {object} position
     * @param {boolean} overrideLocationCheck
     * Defualt: false
     * @param {undefined|null|function} success
     * Default: EzFunction.dummyFunction
     * @param {undefined|null|function} failure
     * Default: EzFunction.dummyFunction
     * @param {undefined|null|string} source
     * Default: 'EZCLOCKER-WEBSITE'
     *
     * @returns {Promise}
     */
    clockOutGeoNonRest(employerId, employeeId, position, overrideLocationCheck = false, success = EzFunction.dummyFunction, failure = EzFunction.dummyFunction,
        source = 'EZCLOCKER-WEBSITE') {
        if (!EzNumber.isNumber(employerId)) {
            throw new EzBadParamException(
                'employerId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.clockOutGeoNonRest);
        }
        if (!EzNumber.isNumber(employeeId)) {
            throw new EzBadParamException(
                'employeeId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.clockOutGeoNonRest);
        }
        if (!EzObject.isValid(position)) {
            throw new EzBadParamException(
                'position',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.clockOutGeoNonRest);
        }

        source = EzString.stringWithLengthOrDefault(
            source,
            'EZCLOCKER-WEBSITE');

        return EzTimeEntryService.ezInstance.ezPostAndHandleResolveRejectCallback(
            globalThis.ezApi.ezclocker.ezNavigation.ezGetInternalClassicApiUrl(
                EzUrl.build`
                    timeEntry/clockOut/${employerId}/${employeeId}
                    ?clockOutISO8601Utc=${globalThis.ezApi.ezclocker.ezDateTime.ezNowAsIso()}
                    &timeZoneId=${EzUrl.encodeURL(globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone)}
                    &longitude=${position.coords.longitude}
                    &latitude=${position.coords.latitude}
                    &accuracy=${position.coords.Accuracy}
                    &overrideLocationCheck=${overrideLocationCheck}
                    &source=${source}`,
                'v1'),
            EzString.EMPTY,
            success,
            failure);
    }

    /**
     * @public @method
     * Obtains a time entry for the provided time entry id.
     *
     * @returns {Promise}
     * Resolve returns an EzEntityResponse object
     * Reject returns an EzEntityResponse object
     */
    ezGetTimeEntry(timeEntryId) {
        if (!EzNumber.isNumber(timeEntryId)) {
            throw new EzBadParamException(
                'timeEntryId',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.clockOutGeoNonRest);
        }

        return globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
            globalThis.ezApi.ezclocker.ezNavigation.ezGetInternalApiUrl(
                `timeentry/${timeEntryId}?timeZoneId=${globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone}`,
                'v2'))
            .then(
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject);
    }

    /**
     * @protected @method
     * Executes a post and handles the resolve/reject/callback.
     * @param {string} url
     * @param {undefined|null|object} payload
     * Default: null
     * @param {function} successCallback
     * Default: EzFunction.dummyFunction
     * @param {function} failureCallback
     * Default: EzFunction.dummyFunction
     *
     * @returns {Promise}
     */
    ezPostAndHandleResolveRejectCallback(url, payload = null, successCallback = EzFunction.dummyFunction, failureCallback = EzFunction.dummyFunction) {
        if (!EzString.hasLength(url)) {
            throw new EzBadParamException(
                'url',
                EzTimeEntryService.ezInstance,
                EzTimeEntryService.ezInstance.clockOutGeoNonRest);
        }

        return EzPromise.promise(
            (resolve, reject) => globalThis.ezApi.ezclocker.ezHttpHelper.post(url, payload)
                .then(
                    (response, jqXHR) => {
                        EzFunction.callback(successCallback, response, jqXHR);

                        return resolve(response, jqXHR);
                    },
                    (eResponse, jqXHR) => {
                        EzFunction.callback(failureCallback, eResponse, jqXHR);

                        return reject(eResponse, jqXHR);
                    }));
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @static
     * @public @readonly @property
     * Gets the name of this class's singleton instance when registered with ezApi.
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezTimeEntryService';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an object of event names that this class may trigger.
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzTimeEntryService_Ready'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the singleton instance of this class that was created by and registerd with EzApi.
     * @type {EzTimeEntryService}
     */
    static #ezInstance = globalThis?.ezApi?.ready && globalThis.ezApi?.ezclocker?.[EzTimeEntryService.ezApiName]
        ? globalThis.ezApi.ezclocker[EzTimeEntryService.ezApiName]
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
     * @returns {EzTimeEntryService}
     */
    static get ezInstance() {
        return EzTimeEntryService.#ezInstance;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the singleton instance of of this class that is registered with EzApi.
     * @param {EzTimeEntryService} instance
     */
    static set ezInstance(instance) {
        if (null != EzTimeEntryService.#ezInstance) {
            throw new Error('EzTimeEntryService\'s singleton instance is already reigstered with EzApi.');
        }

        EzTimeEntryService.#ezInstance = instance;
    }

    /**
     * @static
     * @private @field
     * Stores the EzApi registration state for this class.
     * Default value is NULL
     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
     * @type {string}
     * A valid enum property value from EzRegistrationState
     */
    static #ezApiRegistrationState = globalThis?.ezApi?.ready && globalThis.ezApi?.ezclocker?.[EzTimeEntryService.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the ezApi registration state of this classes's singleton instance.
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzTimeEntryService.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzTimeEntryService.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
    * @surpress
    * @static
    * @private @readonly @property
    * Gets true when all required dependencies for this class report ready.
    * In otherwords, the require dependency's singleton instance is created
    * and is registered with and accessable from EzApi with ezApi.ezclocker.{dependency_name}.
    * @returns {boolean}
    */
    static get #ezCanRegister() {
        return 'PENDING' === EzTimeEntryService.ezApiRegistrationState &&
            globalThis?.ezApi?.ready && globalThis?.ezApi?.ezclocker &&
            globalThis.ezApi.ezclocker?.[EzDateTime.ezApiName]?.ready &&
            globalThis.ezApi.ezclocker?.[EzHttpHelper.ezApiName]?.ready &&
            globalThis.ezApi.ezclocker?.[EzNavigation.ezApiName]?.ready;
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return null != EzTimeEntryService.ezInstance &&
            EzRegistrationState.REGISTERED === EzTimeEntryService.ezApiRegistrationState;
    }


    /**
     * @static
     * @private @method
     * Attempts to register the singleton instance for this class with ezApi. Returns true
     * if successful, false otherwise.
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (EzTimeEntryService.#ezCanRegister && !EzTimeEntryService.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(EzTimeEntryService, EzTimeEntryService.ezApiName);
        }

        return EzTimeEntryService.#ezIsRegistered;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzTimeEntryService.ezApiName
     *     2) Property getter EzTimeEntryService.ezEventNames
     *     3) Property getter EzTimeEntryService.ezInstance
     *     4) Property setter EzTimeEntryService.ezInstance
     *     5) Property getter EzTimeEntryService.ezApiRegistrationState
     *     6) Property setter EzTimeEntryService.ezApiRegistrationState
     *     7) Property getter EzTimeEntryService.#ezCanRegister
     *     8) Property getter EzTimeEntryService.#ezIsRegistered
     *     9) Method EzTimeEntryService.#ezRegistrator()
     */
    static {
        if (!EzTimeEntryService.#ezIsRegistered) {
            EzTimeEntryService.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzTimeEntryService.#ezRegistrator()) {
                document.addEventListener(
                    EzTimeEntryService.ezOnEzApiReadyEventName,
                    EzTimeEntryService.#ezRegistrator);

                document.addEventListener(
                    EzDateTime.ezEventNames.onReady,
                    EzTimeEntryService.#ezRegistrator);

                document.addEventListener(
                    EzHttpHelper.ezEventNames.onReady,
                    EzTimeEntryService.#ezRegistrator);

                document.addEventListener(
                    EzNavigation.ezEventNames.onReady,
                    EzTimeEntryService.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
